import DOMPurify from 'dompurify';

// Regex matches <script type="application/ld+json">...</script> across newlines.
// Applied client-side so any lingering inline JSON-LD in old blog content is
// stripped from the visible body even if backend migration missed it.
const JSONLD_SCRIPT_RE = /<script[^>]*type\s*=\s*["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi;

// Match any <style>...</style> block (with optional attributes). Applied
// client-side because DOMPurify's default 'html' profile strips the <style>
// wrapper but keeps its text content — leaking the CSS body as visible plain
// text on the blog page. See sanitize.js RCA (Feb 07, 2026 P0 fix).
const STYLE_TAG_RE = /<style\b[^>]*>[\s\S]*?<\/style>/gi;

// Fallback: match an UNCLOSED <style> tag (missing </style>) and strip
// everything from it up to the next block-level HTML tag or end of string.
// Guards against LLM output that accidentally emits `<style>` without a
// matching closer (rare, but has been seen with truncated generations).
const STYLE_TAG_UNCLOSED_RE = /<style\b[^>]*>[\s\S]*?(?=<(?:article|section|div|h[1-6]|p|ul|ol|table|figure|nav|header|footer|main|aside)\b)/i;

// Orphan CSS rule block: a bare CSS selector (`.foo`, `#bar`, `article.foo`)
// followed by a `{ ... }` declaration block, NOT wrapped in <style>. This
// catches cases where a prior sanitizer already unwrapped the <style> tag
// (KEEP_CONTENT leak), or an admin pasted raw CSS text into the editor.
// We repeatedly strip these while they appear at the beginning of the body
// so headings/paragraphs are never mistaken for CSS. Never touches text
// inside <p>/<h*>/<li> etc. because those don't start with `.`/`#`.
const ORPHAN_CSS_BLOCK_RE = /^\s*(?:[.#][A-Za-z_][\w-]*(?:\s*[>+~,]\s*[.#]?[A-Za-z_][\w-]*(?:\.[\w-]+)*|\s+[.#]?[A-Za-z_][\w-]*(?:\.[\w-]+)*|:[\w-]+(?:\([^)]*\))?)*\s*\{[^{}]*\}\s*)+/;
// CSS @media / @keyframes / @font-face / @import blocks (with balanced braces
// approximated up to two levels of nesting — enough for typical @media rules).
const ORPHAN_AT_RULE_RE = /^\s*@(?:media|keyframes|font-face|import|charset|supports)\b[^{;]*(?:\{(?:[^{}]|\{[^{}]*\})*\}|;)\s*/;

/**
 * Strip <script type="application/ld+json"> blocks (with content) from `html`.
 * Safe on null/undefined. Never touches other tags.
 */
export function stripInlineJsonLd(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(JSONLD_SCRIPT_RE, '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Strip <style>...</style> blocks (tag + content) from `html`.
 *
 * Why this exists: DOMPurify with `USE_PROFILES: { html: true }` removes the
 * <style> element but preserves its text content (KEEP_CONTENT default), which
 * leaks the raw CSS rules onto the page as visible plain text. Removing the
 * whole block up-front side-steps that default and lets us serve the blog
 * theme from a permanent stylesheet (`styles/blog-content.css`) instead.
 * Safe on null/undefined.
 */
export function stripStyleBlocks(html) {
  if (!html || typeof html !== 'string') return '';
  let out = html;
  // 1. Well-formed <style>...</style>
  out = out.replace(STYLE_TAG_RE, '');
  // 2. Unclosed <style> (missing closer) — strip up to next block-level tag
  out = out.replace(STYLE_TAG_UNCLOSED_RE, '');
  // 3. Orphan CSS rule blocks at the very start (repeated until none remain).
  //    Only strips leading matches so paragraphs in the article body are safe.
  //    Also strips @media/@keyframes/@font-face at-rules with balanced braces.
  let prev;
  let guard = 20;  // Prevent infinite loop on pathological input
  do {
    prev = out;
    out = out.replace(ORPHAN_AT_RULE_RE, '');
    out = out.replace(ORPHAN_CSS_BLOCK_RE, '');
    guard -= 1;
  } while (out !== prev && guard > 0);
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Centralised HTML sanitiser for all `dangerouslySetInnerHTML` usage.
 *
 * Default profile = safe-for-rich-text:
 *   - allows common formatting (b/i/u/em/strong/p/br/h1-h6/ul/ol/li/a/blockquote/code/pre/table/img)
 *   - strips <script>, on*, javascript: hrefs, style with expressions, iframes, etc.
 *
 * Usage:
 *   import { safeHtml } from '../lib/sanitize';
 *   <div dangerouslySetInnerHTML={safeHtml(blog.content_html)} />
 */
export function sanitizeHtml(dirty) {
  if (dirty == null) return '';
  // Pre-strip inline JSON-LD scripts AND <style> blocks (both tag + content)
  // BEFORE handing off to DOMPurify. DOMPurify's default 'html' profile
  // removes these tags but preserves their inner text, which leaks
  // JSON payloads / raw CSS rules into the visible body.
  const preClean = stripStyleBlocks(stripInlineJsonLd(String(dirty)));
  return DOMPurify.sanitize(preClean, {
    // Extend the default html profile with the svg profile so inline SVG icons
    // (used in the blog social-share strip since Feb 07, 2026) survive the
    // sanitiser. onclick/onload handlers remain forbidden via FORBID_ATTR.
    USE_PROFILES: { html: true, svg: true, svgFilters: false },
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Convenience helper returning the dangerouslySetInnerHTML object shape directly.
 *   <div {...safeHtml(content)} />
 */
export function safeHtml(dirty) {
  return { __html: sanitizeHtml(dirty) };
}

/**
 * Safe JSON-LD serialiser for `<script type="application/ld+json">` tags.
 *
 * Escapes `<` to `\u003c` so a value containing `</script>` cannot break out
 * of the surrounding script element. Returns the dangerouslySetInnerHTML shape.
 *
 *   <script type="application/ld+json" {...safeJsonLd({ '@context': '...' })} />
 */
export function safeJsonLd(obj) {
  const json = JSON.stringify(obj).replace(/</g, '\\u003c');
  return { __html: json };
}
